import React from 'react'
import styled from 'styled-components'
import { useLogic } from '~common/ui-logic/hooks/logic'
import { useAppContext } from '~/app-context'
import { LoginLogic, type LoginLogicDependencies } from './logic'
import { Button } from '~common/components/button'
import { TextField } from '~common/components/text-field'
import { LoadingIndicator } from '~common/components/loading'
import { ErrorBox } from '~/features/error-box'
import type { Theme } from '~common/theme/types'

export interface LoginProps extends LoginLogicDependencies {}

export function Login(props: LoginProps) {
    const appContext = useAppContext()
    const { logic, state } = useLogic(LoginLogic, {
        ...props,
        ...appContext,
    })

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (state.mode === 'login') {
            logic.handleEmailLogin()
        } else {
            logic.handleEmailRegister()
        }
    }

    const popupBlockedScreen = () => (
        <Container>
            <Title>Popups Blocked</Title>
            <InfoBox>
                <InfoText>
                    To continue with Google sign-in, please allow popups for
                    this site in your browser settings. Once allowed, click the
                    button below to recheck.
                </InfoText>
            </InfoBox>
            <Button
                onClick={logic.recheckPopupBlocked}
                label="I've allowed popups"
                size="medium"
            />
        </Container>
    )

    return (
        <Container>
            <Card>
                <Title>
                    {state.mode === 'login' ? 'Sign In' : 'Create Account'}
                </Title>

                <ModeSwitcher>
                    <ModeButton
                        active={state.mode === 'login'}
                        onClick={() => logic.setMode('login')}
                    >
                        Sign In
                    </ModeButton>
                    <ModeButton
                        active={state.mode === 'register'}
                        onClick={() => logic.setMode('register')}
                    >
                        Sign Up
                    </ModeButton>
                </ModeSwitcher>

                {state.error && (
                    <ErrorContainer>
                        <ErrorBox errorMessage={state.error} />
                    </ErrorContainer>
                )}

                <Form onSubmit={handleSubmit}>
                    <InputGroup>
                        <TextField
                            type="email"
                            placeholder="Email"
                            initValue={state.email}
                            onChange={(value) => logic.setEmail(value)}
                            editable={state.loadState !== 'running'}
                            size="medium"
                            icon="mail"
                            autoFocus
                        />
                    </InputGroup>

                    <InputGroup>
                        <TextField
                            type="password"
                            placeholder="Password"
                            initValue={state.password}
                            onChange={(value) => logic.setPassword(value)}
                            editable={state.loadState !== 'running'}
                            size="medium"
                        />
                    </InputGroup>

                    {state.mode === 'register' && (
                        <InputGroup>
                            <TextField
                                type="password"
                                placeholder="Confirm Password"
                                initValue={state.confirmPassword}
                                onChange={(value) =>
                                    logic.setConfirmPassword(value)
                                }
                                editable={state.loadState !== 'running'}
                                size="medium"
                            />
                        </InputGroup>
                    )}

                    <Button
                        type="submit"
                        variant="primary"
                        size="large"
                        fullWidth
                        loading={state.loadState === 'running'}
                        disabled={state.loadState === 'running'}
                        label={
                            state.loadState === 'running'
                                ? undefined
                                : state.mode === 'login'
                                  ? 'Sign In'
                                  : 'Create Account'
                        }
                    />

                    <Divider>
                        <DividerLine />
                        <DividerText>or</DividerText>
                        <DividerLine />
                    </Divider>

                    {state.popupBlocked ? (
                        popupBlockedScreen()
                    ) : (
                        <Button
                            variant="outline"
                            size="large"
                            fullWidth
                            disabled={state.loadState === 'running'}
                            onClick={logic.handleGoogleAuth}
                            icon="gmail"
                            label={
                                state.loadState === 'running'
                                    ? undefined
                                    : 'Continue with Google'
                            }
                        />
                    )}
                </Form>
            </Card>
        </Container>
    )
}

const Container = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    background-color: ${({ theme }) => (theme as Theme).colors.background.bg1};
    box-sizing: border-box;
`

const Card = styled.div`
    width: 100%;
    max-width: 400px;
    background-color: ${({ theme }) => (theme as Theme).colors.background.bg2};
    border-radius: ${({ theme }) => (theme as Theme).borderRadius['2xl']};
    padding: ${({ theme }) => (theme as Theme).spacing[10]};
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    box-sizing: border-box;
`

const Title = styled.h1`
    font-size: ${({ theme }) => (theme as Theme).typography.fontSize['3xl']};
    font-weight: ${({ theme }) => (theme as Theme).typography.fontWeight.bold};
    color: ${({ theme }) => (theme as Theme).colors.text.primary};
    text-align: center;
    margin: 0 0 ${({ theme }) => (theme as Theme).spacing[8]} 0;
`

const ModeSwitcher = styled.div`
    display: flex;
    gap: ${({ theme }) => (theme as Theme).spacing[2]};
    margin-bottom: ${({ theme }) => (theme as Theme).spacing[8]};
    background-color: ${({ theme }) => (theme as Theme).colors.background.bg1};
    border-radius: ${({ theme }) => (theme as Theme).borderRadius.full};
    padding: ${({ theme }) => (theme as Theme).spacing[1]};
`

const ModeButton = styled.button<{ active: boolean }>`
    flex: 1;
    padding: ${({ theme }) => (theme as Theme).spacing[2]}
        ${({ theme }) => (theme as Theme).spacing[4]};
    border: none;
    background-color: ${({ active, theme }) =>
        active ? (theme as Theme).colors.background.bg2 : 'transparent'};
    color: ${({ theme }) => (theme as Theme).colors.text.primary};
    border-radius: ${({ theme }) => (theme as Theme).borderRadius.full};
    font-size: ${({ theme }) => (theme as Theme).typography.fontSize.base};
    font-weight: ${({ active, theme }) =>
        active
            ? (theme as Theme).typography.fontWeight.bold
            : (theme as Theme).typography.fontWeight.normal};
    cursor: pointer;
    transition: all ${({ theme }) => (theme as Theme).transitions.normal};

    &:hover {
        background-color: ${({ active, theme }) =>
            active
                ? (theme as Theme).colors.background.bg2
                : (theme as Theme).colors.background.bg1};
    }
`

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => (theme as Theme).spacing[6]};
`

const InputGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => (theme as Theme).spacing[2]};
`

const ErrorContainer = styled.div`
    margin-bottom: ${({ theme }) => (theme as Theme).spacing[4]};
`

const Divider = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => (theme as Theme).spacing[4]};
    margin: ${({ theme }) => (theme as Theme).spacing[4]} 0;
`

const DividerLine = styled.div`
    flex: 1;
    height: 1px;
    background-color: ${({ theme }) => (theme as Theme).colors.border.primary};
`

const DividerText = styled.span`
    color: ${({ theme }) => (theme as Theme).colors.text.secondary};
    font-size: ${({ theme }) => (theme as Theme).typography.fontSize.sm};
`

const InfoBox = styled.div`
    background-color: ${({ theme }) => (theme as Theme).colors.greyScale[300]};
    padding: ${({ theme }) => (theme as Theme).spacing[4]};
    border-radius: ${({ theme }) => (theme as Theme).borderRadius.md};
    margin-bottom: ${({ theme }) => (theme as Theme).spacing[6]};
`

const InfoText = styled.p`
    font-size: ${({ theme }) => (theme as Theme).typography.fontSize.sm};
    color: ${({ theme }) => (theme as Theme).colors.text.secondary};
    margin: 0;
    line-height: 1.5;
    text-align: center;
`
